// ═══════════════════════════════════════════════════════════════════════════
// TIMESHEET TRANSFORMER - Centralized Date Format Handling
// ═══════════════════════════════════════════════════════════════════════════

import moment from 'moment';
import { ITimeSheet, ITimeSheetDetail } from './invoice.interface';
import { ITimesheetInternal, ITimesheetDetailInternal, ITimesheetSubmitDTO } from './timesheet.dto';

/**
 * Centralized transformer for timesheet data.
 * Handles all date format conversions in one place.
 */
export class TimesheetTransformer
{
    // ═══════════════════════════════════════════════════════════════════════
    // DATE FORMAT CONSTANTS
    // ═══════════════════════════════════════════════════════════════════════

    private static readonly API_FORMAT = 'YYYY-MM-DD';
    private static readonly CALENDAR_KEY_FORMAT = 'MM-DD-YYYY';
    private static readonly DISPLAY_FORMAT = 'MMM DD, YYYY';

    // ═══════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Transform API timesheet response to internal format with pre-computed maps.
     * This is called in the delegate layer after API response.
     */
    static toInternal(apiTimesheet: ITimeSheet): ITimesheetInternal
    {
        if (!apiTimesheet)
        {
            return null;
        }

        const details = this.transformDetails(apiTimesheet.timeSheetDetails || []);
        const { hoursMap, commentsMap } = this.buildCalendarMaps(details);

        return {
            ...apiTimesheet,
            timeSheetDetails: details,
            hoursMap,
            commentsMap,
            fromDateDisplay: this.formatForDisplay(apiTimesheet.fromDate),
            toDateDisplay: this.formatForDisplay(apiTimesheet.toDate),
            fromDateApi: this.formatForAPI(apiTimesheet.fromDate),
            toDateApi: this.formatForAPI(apiTimesheet.toDate)
        };
    }

    /**
     * Transform internal format to API submission format.
     * Used when submitting/updating timesheets.
     */
    static toApiSubmission(
        projectCodeId: string,
        fromDate: Date,
        toDate: Date,
        totalHours: number,
        details: Array<{ dateOfWork: Date; hours: number; comments?: string; timeSheetDetailId?: string }>,
        files?: File[],
        timeSheetId?: string
    ): ITimesheetSubmitDTO
    {
        return {
            ProjectCodeId: projectCodeId,
            TimeSheetId: timeSheetId,
            FromDate: this.formatForAPI(fromDate),
            ToDate: this.formatForAPI(toDate),
            TotalHours: totalHours,
            TimeSheetDetails: details.map(d => ({
                timeSheetDetailId: d.timeSheetDetailId,
                dateOfWork: this.formatForAPI(d.dateOfWork),
                hours: d.hours || 0,
                comments: d.comments || ''
            })),
            Files: files,
            clientTimeStamp: new Date().toISOString()
        };
    }

    /**
     * Update an existing detail in the internal model.
     * Used when user edits hours/comments in the calendar.
     */
    static updateDetail(
        details: ITimesheetDetailInternal[],
        calendarKey: string,
        hours: number,
        comments?: string
    ): ITimesheetDetailInternal[]
    {
        const existing = details.find(d => d.dateOfWorkCalendarKey === calendarKey);

        if (existing)
        {
            existing.hours = hours;
            existing.comments = comments || existing.comments || '';
        }
        else
        {
            // Create new detail from calendar key
            // Parse as local date and set to midnight to avoid timezone issues
            const dateObj = moment(calendarKey, this.CALENDAR_KEY_FORMAT).startOf('day').toDate();
            details.push({
                dateOfWork: dateObj,
                dateOfWorkApi: this.formatForAPI(dateObj),
                dateOfWorkCalendarKey: calendarKey,
                dateOfWorkDisplay: this.formatForDisplay(dateObj),
                hours: hours || 0,
                comments: comments || ''
            });
        }

        return details;
    }

    /**
     * Rebuild calendar maps from detail array.
     * Used when details are modified and maps need refresh.
     */
    static rebuildMaps(details: ITimesheetDetailInternal[]): { hoursMap: { [key: string]: number }; commentsMap: { [key: string]: string } }
    {
        return this.buildCalendarMaps(details);
    }

    /**
     * Format a Date as YYYY-MM-DD (API format).
     * Public helper for components that need to format dates for native HTML date inputs.
     */
    static toApiDateString(date: Date | string): string
    {
        return this.formatForAPI(date);
    }

    /**
     * Format a Date as MM-DD-YYYY (calendar key format).
     * Public helper for components that need calendar key strings.
     */
    static toCalendarKey(date: Date | string): string
    {
        return this.formatForCalendarKey(date);
    }

    // ═══════════════════════════════════════════════════════════════════════
    // PRIVATE HELPERS
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Transform detail array, computing all date formats once.
     */
    private static transformDetails(apiDetails: ITimeSheetDetail[]): ITimesheetDetailInternal[]
    {
        return apiDetails.map(d => ({
            timeSheetDetailId: d.timeSheetDetailId,
            dateOfWork: this.ensureDate(d.dateOfWork),
            dateOfWorkApi: this.formatForAPI(d.dateOfWork),
            dateOfWorkCalendarKey: this.formatForCalendarKey(d.dateOfWork),
            dateOfWorkDisplay: this.formatForDisplay(d.dateOfWork),
            hours: d.hours || 0,
            comments: d.comments || '',
            status: d.status
        }));
    }

    /**
     * Build hoursMap and commentsMap keyed by MM-DD-YYYY.
     */
    private static buildCalendarMaps(details: ITimesheetDetailInternal[]): { hoursMap: { [key: string]: number }; commentsMap: { [key: string]: string } }
    {
        const hoursMap: { [key: string]: number } = {};
        const commentsMap: { [key: string]: string } = {};

        details.forEach(d =>
        {
            hoursMap[d.dateOfWorkCalendarKey] = d.hours;
            commentsMap[d.dateOfWorkCalendarKey] = d.comments;
        });

        return { hoursMap, commentsMap };
    }

    // ═══════════════════════════════════════════════════════════════════════
    // DATE FORMATTING UTILITIES
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * PUBLIC: Format a Date as MM-DD-YYYY (calendar key format).
     * Use this when building calendar maps in components.
     * Uses local date components to prevent timezone-related date shifts.
     */
    public static toKey(date: Date | string): string
    {
        if (!date) { return ''; }
        // Use local time to preserve the date as-is without timezone conversion
        const m = moment(date);
        return m.format(this.CALENDAR_KEY_FORMAT);
    }

    /**
     * Format date as YYYY-MM-DD (API format).
     * Uses local time to preserve the date as-is.
     */
    private static formatForAPI(date: Date | string): string
    {
        if (!date) { return ''; }
        return moment(date).format(this.API_FORMAT);
    }

    /**
     * Format date as MM-DD-YYYY (calendar key format).
     * Uses local time to preserve the date as-is.
     */
    private static formatForCalendarKey(date: Date | string): string
    {
        if (!date) { return ''; }
        return moment(date).format(this.CALENDAR_KEY_FORMAT);
    }

    /**
     * Format date for display (e.g., "Jan 15, 2024").
     */
    private static formatForDisplay(date: Date | string): string
    {
        if (!date) { return ''; }
        return moment(date).format(this.DISPLAY_FORMAT);
    }

    /**
     * Ensure value is a Date object.
     * API sends date-only strings (YYYY-MM-DD) without timestamps.
     * Parse as local date to prevent timezone shifts.
     */
    private static ensureDate(value: Date | string): Date
    {
        if (value instanceof Date) { return value; }

        // Parse as local date using moment to avoid timezone issues
        // API format is YYYY-MM-DD, we want local midnight on that date
        return moment(value, this.API_FORMAT).toDate();
    }
}
